import React from "react";

import cn from "classnames";
import { useIntl } from "react-intl";

import localSettings from "@app/config/LocalSettings";
import FunnelSummaryInfo from "@app/scene/deal/type/FunnelSummaryInfo";

import AdjustedAmountTooltip from "./AdjustedAmountTooltip";
import styles from "./TotalValues.module.scss";

interface Props {
  classNames?: {
    adjustedAmount?: string;
    container?: string;
    dealsCount?: string;
    openAmount?: string;
    values?: string;
  };
  dealsCount: number;
  funnelSummary: FunnelSummaryInfo;
  subtitle?: string;
}

const TotalValues: React.FC<Props> = ({ classNames, dealsCount, funnelSummary, subtitle }) => {
  const intl = useIntl();

  return (
    <div className={cn(styles.container, classNames?.container)}>
      <div className={styles.title}>
        {intl.formatMessage({
          id: "deal.totalValues.title",
          defaultMessage: "Aggregate Deal Information",
          description: "Title of total values",
        })}
        {subtitle && <div className={styles.subtitle}>{subtitle}</div>}
      </div>
      <div className={cn(styles.values, classNames?.values)}>
        <div>
          {intl.formatMessage(
            {
              id: "deal.totalValues.dealsCount",
              defaultMessage: "Total # Deals: {value}",
              description: "Deal card view. Total values. Deals count",
            },
            { value: <b className={classNames?.dealsCount}>{dealsCount}</b> }
          )}
        </div>
        <div>
          {intl.formatMessage(
            {
              id: "deal.totalValues.openAmount",
              defaultMessage: "Total Open Amount: {value}",
              description: "Deal card view. Total values. Open amount",
            },
            {
              value: (
                <span className={classNames?.openAmount}>
                  {intl.formatNumber(funnelSummary.openAmount, {
                    currency: localSettings.getCurrencyCode(),
                    maximumFractionDigits: 2,
                    minimumFractionDigits: 2,
                    style: "currency",
                  })}
                </span>
              ),
            }
          )}
        </div>
        <div>
          {intl.formatMessage(
            {
              id: "deal.totalValues.adjustedAmount",
              defaultMessage: "Total Adjusted Amount: {value}",
              description: "Deal card view. Total values. Adjusted amount",
            },
            {
              value: (
                <AdjustedAmountTooltip>
                  <b className={cn(styles.adjustedAmount, classNames?.adjustedAmount)}>
                    {intl.formatNumber(funnelSummary.adjustedAmount, {
                      currency: localSettings.getCurrencyCode(),
                      maximumFractionDigits: 2,
                      minimumFractionDigits: 0,
                      style: "currency",
                    })}
                  </b>
                </AdjustedAmountTooltip>
              ),
            }
          )}
        </div>
      </div>
    </div>
  );
};

export default TotalValues;
